import { getWindowOrThrow } from "@faire/web/common/globals/getWindow";
import { useRefFrom } from "@faire/web/common/hooks/useRefFrom";
import * as React from "react";

import { AbortNotifier } from "../userPerceivedLoadTiming/AbortNotifier";
import { waitForImagesToLoad } from "../userPerceivedLoadTiming/waitForImagesToLoad";

const DEFAULT_IMAGE_LOADING_TIMEOUT = 5_000;

export const useWaitForImagesToLoad = (options: {
  numComponents: number;
  onImageLoad: (numImagesPending: number, totalNumImages: number) => void;
  onTimeout?: () => void;
  shouldIncludeAllImages?: boolean;
  timeout?: number;
}) => {
  const optionsRef = React.useRef(options);
  optionsRef.current = options;
  const mountTimerId = React.useRef<number | undefined>(undefined);

  const { current: notifier } = useRefFrom(() => new AbortNotifier());

  React.useEffect(() => {
    return () => {
      // cleanup image listeners on unmount
      notifier.abort();
    };
  }, [notifier]);

  const elementListRef = React.useRef<HTMLElement[]>([]);
  const startWaitForImagesToLoad = () => {
    const elementList = elementListRef.current;
    void waitForImagesToLoad(
      elementList,
      notifier,
      (numImagesPending: number, totalNumImages: number) =>
        optionsRef.current.onImageLoad(numImagesPending, totalNumImages),
      optionsRef.current.shouldIncludeAllImages
    ).finally(() => {
      optionsRef.current.onImageLoad(0, 0);
      notifier.abort();
    });
  };

  React.useEffect(() => {
    if (elementListRef.current.length >= options.numComponents) {
      startWaitForImagesToLoad();
    }
  }, [options.numComponents]);

  return React.useCallback(
    (element: HTMLElement | null) => {
      if (element) {
        const { numComponents, onTimeout } = optionsRef.current;
        if (mountTimerId.current === undefined) {
          mountTimerId.current = getWindowOrThrow().setTimeout(() => {
            onTimeout?.();
            notifier.abort();
          }, optionsRef.current.timeout ?? DEFAULT_IMAGE_LOADING_TIMEOUT);
          notifier.addAbortListener(() => {
            clearTimeout(mountTimerId.current);
          });
        }
        const elementList = elementListRef.current;
        elementList.push(element);
        if (elementList.length === numComponents) {
          startWaitForImagesToLoad();
        }
      }
    },
    [notifier]
  );
};
